import { Select } from 'antd'
import { cn } from '@/utils/cn'
import type { MaterialSkuOption } from '@/utils/materialMaster'

export type { MaterialSkuOption }

type MaterialSkuSelectProps = {
  value: string
  options: MaterialSkuOption[]
  onChange: (sku: string) => void
  placeholder?: string
  loading?: boolean
  disabled?: boolean
  allowClear?: boolean
  size?: 'small' | 'middle' | 'large'
  className?: string
  compact?: boolean
}

/**
 * Searchable material-master dropdown for Engine Tag SKU assignment.
 * Closed state: code + truncated description. Open list: two-line options.
 */
export function MaterialSkuSelect({
  value,
  options,
  onChange,
  placeholder = 'Search material / SKU',
  loading = false,
  disabled = false,
  allowClear = true,
  size = 'middle',
  className,
  compact = false,
}: MaterialSkuSelectProps) {
  const selected = options.find((o) => o.value === value)

  return (
    <Select
      showSearch
      allowClear={allowClear}
      value={value || undefined}
      options={options}
      loading={loading}
      disabled={disabled}
      size={size}
      placeholder={placeholder}
      optionFilterProp="label"
      listHeight={320}
      popupMatchSelectWidth={false}
      styles={{
        popup: {
          root: {
            minWidth: Math.max(280, compact ? 300 : 340),
            maxWidth: 'min(92vw, 420px)',
          },
        },
      }}
      data-no-wedge="true"
      className={cn(
        'rfid-material-select w-full',
        compact && 'rfid-material-select--compact',
        !selected && 'rfid-material-select--empty',
        className
      )}
      filterOption={(input, option) => {
        const hay = String(option?.label ?? option?.value ?? '').toLowerCase()
        return hay.includes(input.trim().toLowerCase())
      }}
      optionRender={(opt) => {
        const data = opt.data as MaterialSkuOption
        return (
          <div className="rfid-material-option py-0.5">
            <div className="font-mono text-[12px] font-extrabold leading-tight text-foreground">
              {data.code || String(opt.value)}
            </div>
            {data.description ? (
              <div className="mt-0.5 line-clamp-2 text-[11px] font-semibold leading-snug text-muted-foreground">
                {data.description}
              </div>
            ) : null}
          </div>
        )
      }}
      labelRender={() =>
        selected ? (
          <span className="rfid-material-select__value inline-flex min-w-0 max-w-full items-baseline gap-1.5">
            <span className="shrink-0 font-mono text-[12px] font-extrabold">{selected.code}</span>
            {selected.description ? (
              <span className="min-w-0 truncate text-[11px] font-semibold text-muted-foreground">
                {selected.description}
              </span>
            ) : null}
          </span>
        ) : (
          <span className="text-muted-foreground">{placeholder}</span>
        )
      }
      onChange={(next) => onChange(typeof next === 'string' ? next : '')}
      notFoundContent={
        <div className="px-2 py-3 text-center text-xs font-semibold text-muted-foreground">
          {loading ? 'Loading materials…' : 'No materials match'}
        </div>
      }
    />
  )
}
